import { ICompanyState } from './company';
import { GetterTree, MutationTree, ActionTree, Commit, ActionContext, Module } from 'vuex';
import { ICompany } from './../api/company.interface';
import { companyService } from '../api/company.api';
import { getFileName, findWithAttr, IVuexState } from '@/store/store';
import { Utilities } from '@/features/base/api/utilities';

export interface ICompanyState {
  companies: ICompany[];
  dirty: boolean;
}

const state: ICompanyState = {
  companies: [],
  dirty: true,
};

const getters: GetterTree<ICompanyState, IVuexState> = {
  companies: (state: ICompanyState) => state.companies,
  dirty: (state: ICompanyState) => state.dirty,
};

const mutations: MutationTree<ICompanyState> = {
  reset: (state: ICompanyState) => {
    state.companies = [];
    state.dirty = true;
  },
  setAll: (state: ICompanyState, companies: ICompany[]) => {
    state.companies = companies;
  },
  delete: (state: ICompanyState, index: number) => {
    state.companies.splice(index, 1);
  },
  add: (state: ICompanyState, company: ICompany) => {
    state.companies.push(company);
  },
  update: (state: ICompanyState, company: ICompany) => {
    const companies = state.companies;
    for (const i in companies) {
      if (companies[i].id === company.id) {
        companies[i] = company;
      }
    }
  },
  setDirty: (state: ICompanyState, dirty: boolean) => {
    state.dirty = dirty;
  },
};

const actions: ActionTree<ICompanyState, IVuexState> = {
  reset: ({ commit }: { commit: Commit }) => {
    commit('reset');
  },

  getAll({ commit, state }: { commit: Commit; state: ICompanyState }) {
    if (state.dirty) {
      return companyService.all().then((response: IApi<ICompany[]>) => {
        commit('setAll', response.result);
        commit('setDirty', false);
      });
    }
  },

  add({ commit }: { commit: Commit }, company: ICompany) {
    return companyService.add(company).then((response: IApi<ICompany>) => {
      commit('add', response.result);
    });
  },

  update({ commit }: { commit: Commit }, company: ICompany) {
    return companyService.update(company).then((response: IApi<ICompany>) => {
      commit('update', company);
    });
  },

  download({ commit }: { commit: Commit }, id: string) {
    return companyService.download(id).then((response: any) => {
      const fileName = Utilities.getFileName(response);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    });
  },

  delete(context: ActionContext<ICompanyState, IVuexState>, id: string) {
    return companyService.delete(id).then((response: IApi<string>) => {
      const index = findWithAttr(context.state.companies, 'id', id);
      context.commit('delete', index);
      context.dispatch('store/getAll', null, { root: true });
    });
  },
};

const namespaced: boolean = true;

export const company: Module<ICompanyState, IVuexState> = { namespaced, state, getters, actions, mutations };
